import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// import { useDispatch } from 'react-redux';
import Service from '../services/Service';
// import { userlogin } from '../../../../Shared/Slice/AuthSlice';
import '../styles/PatientList.css';
import showToast from '../../../../Shared/Utils/ToastNotification';
import SuggestMedicationModal from '../components/SuggestMedicationModal';
import SuggestSupplementModal from '../components/SuggestSupplementModal';
import NextStepCardsModal from '../components/NextStepCardsModal';
import JotformAnswersModal from '../components/JotformAnswersModal';
import { axiosInstance } from '../../../../Interceptors/axiosInterceptor';

// Fallback only — the API returns the fully resolved registration URL (the portal
// base differs between B2C and B2B patients).
const buildSignupLink = (token) => {
  const base = (process.env.REACT_APP_PATIENT_PORTAL_URL || '').replace(/\/+$/, '');
  if (!base || !token) return '';
  return `${base}/sign-up/${token}`;
};

const PatientList = () => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [supplementPatient, setSupplementPatient] = useState(null);
  const [nextStepPatient, setNextStepPatient] = useState(null);
  const [selectedJotformPatient, setSelectedJotformPatient] = useState(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [modalLink, setModalLink] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  const [lockingPatientId, setLockingPatientId] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadPatient, setUploadPatient] = useState(null);
  const [uploadType, setUploadType] = useState('mailman');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();
  // const dispatch = useDispatch();

  const fetchPatients = async (pageNum = 1, query = '') => {
    setLoading(true);
    setError(null);
    try {
      const res = await Service.getPatientList({ page: pageNum, search: query });
      setPatients(res.data.results.results || []);
      setTotalCount(res.data.results.total_patients || 0);
      setNextPage(res.data.next);
      setPrevPage(res.data.previous);
      setPage(pageNum);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  // const handleStopImpersonation = async () => {
  //   try {
  //     const originalToken = sessionStorage.getItem('original_admin_token');
  //     if (!originalToken) {
  //       showToast('error', 'No admin session to restore.');
  //       return;
  //     }

  //     const originalRefresh = sessionStorage.getItem('original_admin_refresh_token');

  //     // Restore admin tokens
  //     sessionStorage.setItem('auth_token', originalToken);
  //     sessionStorage.setItem('access_token', originalToken);
  //     if (originalRefresh) sessionStorage.setItem('refresh_token', originalRefresh);

  //     // Clear impersonation flags
  //     sessionStorage.removeItem('impersonating');
  //     sessionStorage.removeItem('impersonator_id');

  //     // Optionally clear stored originals after restoration
  //     sessionStorage.removeItem('original_admin_token');
  //     sessionStorage.removeItem('original_admin_refresh_token');

  //     // Update redux auth state with restored token
  //     await dispatch(
  //       userlogin({
  //         token: originalToken,
  //         user: {},
  //         justRegistered: false,
  //       })
  //     );

  //     // setIsImpersonating(false);
  //     showToast('success', 'Restored admin session.');
  //     navigate('/admin/dashboard');
  //   } catch (err) {
  //     const msg = err?.message || 'Failed to restore admin session';
  //     showToast('error', msg);
  //   }
  // };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!uploadFile || !uploadPatient) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('pdf_file', uploadFile);
    formData.append('order', uploadPatient.latest_order_id);
    formData.append('title', `Lab Result for Order ${uploadPatient.latest_order_id}`);

    // Add dummy mailman/tasso order ids since the webhook normally provides them
    if (uploadType === 'mailman') {
      formData.append('mailman_order_id', 'DEV-' + Math.floor(Math.random() * 10000));
    } else {
      formData.append('tasso_order_id', 'DEV-' + Math.floor(Math.random() * 10000));
    }

    try {
      await axiosInstance.post('/api/reports/upload-pdf/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      showToast('success', 'Lab result uploaded successfully. AI analysis triggered.');
      setShowUploadModal(false);
      setUploadFile(null);
      setUploadPatient(null);
    } catch (err) {
      console.error(err);
      showToast('error', 'Failed to upload lab result');
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    fetchPatients(page, searchQuery);
  }, [page, searchQuery]);

  const handleSearch = () => {
    setPage(1);
    setSearchQuery(searchTerm.trim());
  };

  const handleReset = () => {
    setSearchTerm('');
    setSearchQuery('');
    setPage(1);
  };

  const handleKeyDown = (e) => e.key === 'Enter' && handleSearch();

  const handleNext = () => nextPage && setPage((prev) => prev + 1);
  const handlePrev = () => prevPage && setPage((prev) => Math.max(prev - 1, 1));

  const handleViewOrders = (patient) => {
    navigate(`/admin/patient-orders/${patient.id}`, { state: { patient } });
  };

  const handleViewLogs = (patient) => {
    navigate(`/audit-logs?patient_id=${patient.id}`, { state: { selectedPatientId: patient.id } });
  };

  const handleImpersonate = async (patient) => {
    try {
      const res = await Service.impersonatePatient(patient.id);
      const data = res?.data;
      if (!data?.access) {
        showToast('error', data?.detail || 'Failed to impersonate');
        return;
      }

      // Store impersonation info for potential future use
      sessionStorage.setItem('impersonating_patient', patient.email);
      if (data?.impersonator_id) sessionStorage.setItem('impersonator_id', data.impersonator_id);

      // Open patient portal in new window with impersonation token
      const patientPortalUrl = `${process.env.REACT_APP_PATIENT_PORTAL_URL}/login?impersonation_token=${data.access}&patient_email=${encodeURIComponent(patient.email)}`;

      showToast('success', `Opening patient portal for ${patient.email}`);

      // Open in new tab/window
      window.open(patientPortalUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Failed to impersonate';
      showToast('error', msg);
    }
  };

  const handleTogglePortalLock = async (patient) => {
    setLockingPatientId(patient.id);
    try {
      const newLockState = !patient.portal_locked;
      await Service.togglePortalLock(patient.id, newLockState);
      setPatients((prev) => prev.map((p) => (p.id === patient.id ? { ...p, portal_locked: newLockState } : p)));
      showToast('success', `Portal ${newLockState ? 'locked' : 'unlocked'} for ${patient.email}`);
    } catch (err) {
      showToast('error', err?.response?.data?.detail || 'Failed to update portal lock');
    } finally {
      setLockingPatientId(null);
    }
  };

  const getLatestOrderVoucherId = async (patientId) => {
    try {
      const res = await Service.getOrderList(patientId);
      const orders = res.data || [];
      if (orders.length > 0) {
        const latestOrder = orders[0];
        return latestOrder.db_order?.voucher_id || latestOrder.voucher_id;
      }
      return null;
    } catch (err) {
      console.error('Failed to fetch orders:', err);
      return null;
    }
  };

  const handleShowLinkModal = async (patient) => {
    const voucherId = await getLatestOrderVoucherId(patient.id);
    if (!voucherId) {
      showToast('error', 'No voucher ID found for this patient');
      return;
    }

    const link = `https://patient.mdintegrations.com/?token=${voucherId}`;
    setModalLink(link);
    setModalTitle('Intake Form Link');
    setShowLinkModal(true);
  };

  const handleShowJotformIntakeLink = async (patient) => {
    try {
      const res = await Service.getIntakeFormLink(patient.id);
      const link = res?.data?.branded_intake_url || res?.data?.secure_jotform_url;
      if (!link) {
        showToast('error', 'Failed to generate JotForm intake link');
        return;
      }
      setModalLink(link);
      setModalTitle('Jot form intake link');
      setShowLinkModal(true);
    } catch (err) {
      showToast('error', err?.response?.data?.detail || 'Failed to generate JotForm intake link');
    }
  };

  const handleShowRegistrationLink = async (patient) => {
    try {
      const res = await Service.getRegistrationUrl(patient.id);
      const data = res?.data || {};
      // B2B (clinic) patients have no shopify_customer_id — they sign up with their
      // patient id, on their own portal. The API resolves both cases for us.
      const token = data.signup_token || data.shopify_customer_id || patient.id;
      const link = data.registration_url || data.signup_link || buildSignupLink(token);
      if (!link) {
        showToast('error', 'Failed to get registration URL');
        return;
      }
      setModalLink(link);
      setModalTitle('Registration Link');
      setShowLinkModal(true);
    } catch (err) {
      showToast('error', err?.response?.data?.detail || 'Failed to get registration URL');
    }
  };

  const copyFromModal = () => {
    navigator.clipboard.writeText(modalLink);
    showToast('success', 'Link copied to clipboard!');
    setShowLinkModal(false);
  };

  if (loading) return <p className="loading">Loading patients...</p>;
  if (error) return <p className="error">Error: {error}</p>;

  return (
    <div className="patient-container">
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1 className="page-title">Patient List</h1>
      </div>

      {/* 🔍 Search Section */}
      <div className="search-bar">
        <input
          type="text"
          placeholder="Search by first name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <button className="btn btn-primary" onClick={handleSearch}>
          Search
        </button>
        <button className="btn btn-secondary" onClick={handleReset}>
          Reset
        </button>
      </div>

      {/* 🧾 Table */}
      <div className="table-container">
        <table className="patient-table">
          <thead>
            <tr>
              <th>Full Name</th>
              <th>Email</th>
              <th>Phone Number</th>
              <th>Profile Creation / MDI Form</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {patients.length > 0 ? (
              patients.map((patient, index) => (
                <tr key={patient.id} className={index % 2 === 0 ? 'even' : 'odd'}>
                  <td>{`${patient.first_name || ''} ${patient.last_name || ''}`.trim()}</td>
                  <td>{patient.email}</td>
                  <td>{patient.phone_number || 'N/A'}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                      {patient.is_active ? <span className="status-active">Yes</span> : <span className="status-inactive">No</span>}
                      <span>/</span>
                      {patient.is_voucher_used ? (
                        <span className="status-active">Completed</span>
                      ) : (
                        <span className="status-inactive">Not Completed</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      <button className="btn btn-success" onClick={() => handleViewOrders(patient)}>
                        View Orders
                      </button>
                      <button className="btn btn-view-logs" onClick={() => handleViewLogs(patient)}>
                        View Logs
                      </button>
                      <button className="btn btn-info" onClick={() => setSelectedPatient(patient)}>
                        Suggest Medication
                      </button>
                      <button className="btn btn-info" onClick={() => setSupplementPatient(patient)}>
                        Suggest OK Capsule Supplements
                      </button>
                      <button className="btn btn-intake-link" onClick={() => handleShowLinkModal(patient)}>
                        Intake Form Link
                      </button>
                      <button className="btn btn-jotform-intake-link" onClick={() => handleShowJotformIntakeLink(patient)}>
                        Jot form intake link
                      </button>
                      <button className="btn btn-view-answers" onClick={() => setSelectedJotformPatient(patient)}>
                        View Intake Answers
                      </button>
                      {!patient.is_active && (
                        <button className="btn btn-registration-link" onClick={() => handleShowRegistrationLink(patient)}>
                          Registration Link
                        </button>
                      )}
                      <button className="btn btn-next-step" onClick={() => setNextStepPatient(patient)}>
                        Next Step Cards
                      </button>
                      <button
                        className={`btn ${patient.portal_locked ? 'btn-unlock-portal' : 'btn-lock-portal'}`}
                        onClick={() => handleTogglePortalLock(patient)}
                        disabled={lockingPatientId === patient.id}
                        title={patient.portal_locked ? 'Unlock Portal' : 'Lock Portal'}
                      >
                        {lockingPatientId === patient.id ? '...' : patient.portal_locked ? 'Unlock Portal' : ' Lock Portal'}
                      </button>
                      {patient.is_active && (
                        <button className="btn btn-warning" onClick={() => handleImpersonate(patient)}>
                          LogIn
                        </button>
                      )}
                      <button
                        className="btn"
                        style={{ backgroundColor: '#8B4513', color: '#fff' }}
                        onClick={() => {
                          setUploadPatient(patient);
                          setShowUploadModal(true);
                        }}
                      >
                        Upload PDF Dev
                      </button>
                      {patient.patient_report_id && (
                        <button
                          className="btn btn-info"
                          style={{ color: '#fff' }}
                          onClick={() => navigate(`/admin/review-queue/${patient.patient_report_id}`)}
                        >
                          Review Report
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="no-data">
                  No patients found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* 📊 Total Info */}
      <div className="bottom-info">
        <p>
          Total Patients: <strong>{totalCount}</strong> | Page: <strong>{page}</strong>
        </p>
      </div>

      {/* ⏩ Pagination */}
      <div className="pagination">
        <button className="btn btn-primary" disabled={!prevPage} onClick={handlePrev}>
          Previous
        </button>
        <button className="btn btn-primary" disabled={!nextPage} onClick={handleNext}>
          Next
        </button>
      </div>
      {selectedPatient && <SuggestMedicationModal patient={selectedPatient} onClose={() => setSelectedPatient(null)} />}
      {supplementPatient && <SuggestSupplementModal patient={supplementPatient} onClose={() => setSupplementPatient(null)} />}
      {nextStepPatient && <NextStepCardsModal patient={nextStepPatient} onClose={() => setNextStepPatient(null)} />}
      {selectedJotformPatient && <JotformAnswersModal patient={selectedJotformPatient} onClose={() => setSelectedJotformPatient(null)} />}

      {showLinkModal && (
        <div className="modal-overlay" onClick={() => setShowLinkModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{modalTitle}</h3>
            <div className="link-display">
              <input type="text" value={modalLink} readOnly style={{ width: '100%', marginBottom: '10px' }} />
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={copyFromModal}>
                Copy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Lab Results Modal */}
      {showUploadModal && uploadPatient && (
        <div className="modal-overlay" onClick={() => !uploading && setShowUploadModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '450px' }}>
            <div className="modal-header d-flex justify-content-between align-items-center mb-4">
              <h4 className="m-0 text-primary">Upload Lab Result</h4>
              <button
                type="button"
                className="btn-close"
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}
                onClick={() => !uploading && setShowUploadModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleUploadSubmit}>
              <div className="mb-3">
                <label className="fw-semibold">Patient:</label>
                <div>
                  {uploadPatient.first_name} {uploadPatient.last_name}
                </div>
                <div className="text-muted small">Order ID: {uploadPatient.latest_order_id || 'No Active Order'}</div>
              </div>

              <div className="mb-3">
                <label className="fw-semibold mb-2">Lab Type</label>
                <select
                  className="form-control"
                  style={{ padding: '8px 12px', width: '100%', border: '1px solid #ced4da', borderRadius: '4px' }}
                  value={uploadType}
                  onChange={(e) => setUploadType(e.target.value)}
                >
                  <option value="mailman">Mailman (Semen Analysis)</option>
                  <option value="tasso">Tasso (Hormone Panel)</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="fw-semibold mb-2">PDF File</label>
                <input
                  type="file"
                  accept=".pdf"
                  className="form-control"
                  style={{ padding: '8px 12px', width: '100%', border: '1px solid #ced4da', borderRadius: '4px' }}
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  required
                />
              </div>

              <div className="d-flex justify-content-end gap-2">
                <button type="button" className="btn btn-secondary" onClick={() => setShowUploadModal(false)} disabled={uploading}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={!uploadFile || uploading || !uploadPatient.latest_order_id}>
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>

              {!uploadPatient.latest_order_id && (
                <div className="text-danger small mt-2 text-center">This patient does not have an active order ID.</div>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientList;
